import { useCallback, useEffect, useRef, useState } from "react";
import * as O from "@/components/Ornaments";
import { useSound } from "@/lib/sound";
import { useT } from "@/lib/i18n";
import {
  GuestPhoto,
  CountdownSection,
  StorySection,
  GallerySection,
  ScheduleSection,
  MapSection,
  RsvpSection,
  GuestbookSection,
  ShareSection,
  GuestFooter,
} from "@/components/sections";
import type { VariantProps } from "./types";

// ═══════════════════════════════════════════════════════════════════════
// BOTANICAL — Sumalak (Persian-miniature wedding for Navruz / spring)
//
// The variant stages a Timurid spring wedding: a hand-drawn floral
// extravaganza in deep emerald and saffron, painted in Cinzel Roman
// caps. The hero opens with a self-blooming SVG floral wreath
// (stroke-dasharray sweep), pomegranate-and-chinor corner ornaments,
// and a full-bleed photo with a subtle emerald multiply wash. Between
// Story and Gallery a "Navruz osh" cultural callout explains the spring
// festival significance. Sound is restrained — chime on RSVP yes,
// tap on RSVP no + share/CTA buttons, swoosh on first hero reveal.
// All motion is gated by prefers-reduced-motion.
// ═══════════════════════════════════════════════════════════════════════

// ─── prefers-reduced-motion hook (no extra deps) ──────────────────────
function usePrefersReducedMotion(): boolean {
  const [reduce, setReduce] = useState<boolean>(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return false;
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  });
  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return;
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const onChange = (e: MediaQueryListEvent) => setReduce(e.matches);
    if (mq.addEventListener) mq.addEventListener("change", onChange);
    else mq.addListener(onChange);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener("change", onChange);
      else mq.removeListener(onChange);
    };
  }, []);
  return reduce;
}

// ─── IntersectionObserver reveal wrapper ──────────────────────────────
interface RevealProps {
  children: React.ReactNode;
  reduce: boolean;
  /** Optional callback fired exactly once when the section first enters view. */
  onReveal?: () => void;
  delay?: number;
}

function Reveal({ children, reduce, onReveal, delay = 0 }: RevealProps) {
  const ref = useRef<HTMLDivElement | null>(null);
  const [shown, setShown] = useState(reduce);
  const firedRef = useRef(false);

  useEffect(() => {
    if (reduce) {
      setShown(true);
      if (!firedRef.current) {
        firedRef.current = true;
        onReveal?.();
      }
      return;
    }
    const el = ref.current;
    if (!el || typeof IntersectionObserver === "undefined") {
      setShown(true);
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setShown(true);
            if (!firedRef.current) {
              firedRef.current = true;
              onReveal?.();
            }
            io.disconnect();
            break;
          }
        }
      },
      { threshold: 0.18, rootMargin: "0px 0px -8% 0px" },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [reduce, onReveal]);

  return (
    <div
      ref={ref}
      style={{
        opacity: shown ? 1 : 0,
        transform: shown ? "translateY(0)" : "translateY(20px)",
        transition: reduce
          ? "none"
          : `opacity 1.0s ease-out ${delay}ms, transform 1.0s ease-out ${delay}ms`,
        willChange: reduce ? undefined : "opacity, transform",
      }}
    >
      {children}
    </div>
  );
}

// ─── Hand-drawn floral corner ornament (pomegranate + chinor sprig) ──
interface CornerProps {
  accent: string;
  saffron: string;
  flipX?: boolean;
  flipY?: boolean;
  size?: number;
}

function FloralCorner({ accent, saffron, flipX = false, flipY = false, size = 220 }: CornerProps) {
  const sx = flipX ? -1 : 1;
  const sy = flipY ? -1 : 1;
  return (
    <svg
      aria-hidden
      width={size}
      height={size}
      viewBox="0 0 220 220"
      style={{ display: "block", overflow: "visible" }}
    >
      <g transform={`translate(${flipX ? 220 : 0} ${flipY ? 220 : 0}) scale(${sx} ${sy})`}>
        {/* Sweeping stem */}
        <path
          d="M 8 8 Q 60 30 80 80 Q 100 130 70 200"
          stroke={accent}
          strokeWidth="1.2"
          fill="none"
          opacity="0.85"
        />
        <path
          d="M 8 8 Q 50 70 110 80 Q 170 90 210 70"
          stroke={accent}
          strokeWidth="0.9"
          fill="none"
          opacity="0.65"
        />

        {/* Chinor (plane-tree) leaf cluster, large */}
        <g transform="translate(70 80)">
          <path
            d="M 0 0 Q -14 -10 -10 -26 Q 0 -20 6 -28 Q 12 -20 22 -26 Q 18 -10 4 0 Z"
            fill={accent}
            fillOpacity="0.32"
            stroke={accent}
            strokeWidth="0.7"
          />
          <line x1="0" y1="0" x2="6" y2="-22" stroke={accent} strokeWidth="0.6" />
        </g>

        {/* Pomegranate */}
        <g transform="translate(120 60)">
          <path
            d="M 0 0 Q -12 6 -12 22 Q -12 38 0 44 Q 12 38 12 22 Q 12 6 0 0 Z"
            fill={saffron}
            fillOpacity="0.55"
            stroke={accent}
            strokeWidth="0.8"
          />
          {/* Crown */}
          <path d="M -3 0 L 0 -6 L 3 0 M -5 -2 L 5 -2" stroke={accent} strokeWidth="0.7" fill="none" />
          {/* Seeds peek */}
          <circle cx="-5" cy="22" r="1.2" fill={accent} />
          <circle cx="0" cy="28" r="1.2" fill={accent} />
          <circle cx="5" cy="22" r="1.2" fill={accent} />
        </g>

        {/* Saffron crocus flowers */}
        <g transform="translate(40 130)">
          <ellipse cx="0" cy="0" rx="3.5" ry="9" fill={saffron} fillOpacity="0.85" transform="rotate(-18)" />
          <ellipse cx="0" cy="0" rx="3.5" ry="9" fill={saffron} fillOpacity="0.7" transform="rotate(18)" />
          <ellipse cx="0" cy="2" rx="3.5" ry="9" fill={saffron} fillOpacity="0.55" />
          <line x1="0" y1="9" x2="0" y2="32" stroke={accent} strokeWidth="0.7" />
        </g>

        {/* Tiny budlets */}
        <circle cx="160" cy="40" r="2.5" fill={saffron} />
        <circle cx="190" cy="58" r="1.8" fill={accent} />
        <circle cx="35" cy="170" r="2" fill={saffron} />
        <circle cx="55" cy="190" r="1.4" fill={accent} />

        {/* Small leaves on the stem */}
        {[
          { x: 30, y: 45, r: -30 },
          { x: 100, y: 110, r: 25 },
          { x: 60, y: 160, r: -20 },
        ].map((p, i) => (
          <ellipse
            key={i}
            cx={p.x}
            cy={p.y}
            rx="3.5"
            ry="8"
            fill={accent}
            fillOpacity="0.4"
            transform={`rotate(${p.r} ${p.x} ${p.y})`}
          />
        ))}
      </g>
    </svg>
  );
}

// ─── Signature animated floral wreath ─────────────────────────────────
// Twelve botanical sprigs radiating from a centre medallion. The
// underlying ring + each sprig draws itself with stroke-dasharray on
// first paint. Reduced-motion fallback shows the wreath fully drawn.
interface WreathProps {
  accent: string;
  saffron: string;
  reduce: boolean;
  size?: number;
}

function FloralWreath({ accent, saffron, reduce, size = 220 }: WreathProps) {
  const [bloomed, setBloomed] = useState(reduce);

  useEffect(() => {
    if (reduce) {
      setBloomed(true);
      return;
    }
    const id = window.requestAnimationFrame(() => setBloomed(true));
    return () => window.cancelAnimationFrame(id);
  }, [reduce]);

  const RING_LEN = 360;
  const SPRIG_LEN = 60;
  const sprigs = 12;

  return (
    <svg
      aria-hidden
      width={size}
      height={size}
      viewBox="0 0 200 200"
      style={{ display: "block" }}
    >
      {/* Outer ring — draws itself */}
      <circle
        cx="100"
        cy="100"
        r="58"
        stroke={accent}
        strokeWidth="0.9"
        fill="none"
        strokeDasharray={RING_LEN}
        strokeDashoffset={bloomed ? 0 : RING_LEN}
        style={{
          transition: reduce ? "none" : "stroke-dashoffset 1.6s cubic-bezier(0.22, 0.61, 0.36, 1) 0.1s",
        }}
      />
      {/* Inner hairline */}
      <circle
        cx="100"
        cy="100"
        r="46"
        stroke={accent}
        strokeWidth="0.4"
        fill="none"
        opacity={bloomed ? 0.5 : 0}
        style={{
          transition: reduce ? "none" : "opacity 0.6s ease-out 1.5s",
        }}
      />

      {/* 12 botanical sprigs */}
      {Array.from({ length: sprigs }).map((_, i) => {
        const angle = (i * 360) / sprigs;
        const isFlower = i % 2 === 0;
        const delay = 0.4 + i * 0.08;
        return (
          <g
            key={i}
            transform={`rotate(${angle} 100 100)`}
            style={{
              opacity: bloomed ? 1 : 0,
              transition: reduce ? "none" : `opacity 0.4s ease-out ${delay}s`,
            }}
          >
            {/* Stem */}
            <line
              x1="100"
              y1="42"
              x2="100"
              y2="22"
              stroke={accent}
              strokeWidth="0.7"
              strokeDasharray={SPRIG_LEN}
              strokeDashoffset={bloomed ? 0 : SPRIG_LEN}
              style={{
                transition: reduce
                  ? "none"
                  : `stroke-dashoffset 0.8s ease-out ${delay}s`,
              }}
            />
            {/* Bud or leaf */}
            {isFlower ? (
              <g
                transform="translate(100 18)"
                style={{
                  transformOrigin: "100px 18px",
                  opacity: bloomed ? 1 : 0,
                  transform: bloomed ? "scale(1)" : "scale(0)",
                  transition: reduce
                    ? "none"
                    : `transform 0.5s cubic-bezier(0.34, 1.56, 0.64, 1) ${delay + 0.5}s, opacity 0.3s ease-out ${delay + 0.5}s`,
                }}
              >
                <ellipse cx="0" cy="0" rx="3" ry="4.5" fill={saffron} />
                <ellipse
                  cx="0"
                  cy="0"
                  rx="3"
                  ry="4.5"
                  fill={saffron}
                  fillOpacity="0.6"
                  transform="rotate(60)"
                />
                <ellipse
                  cx="0"
                  cy="0"
                  rx="3"
                  ry="4.5"
                  fill={saffron}
                  fillOpacity="0.6"
                  transform="rotate(-60)"
                />
                <circle cx="0" cy="0" r="1" fill={accent} />
              </g>
            ) : (
              <g
                transform="translate(100 22)"
                style={{
                  transformOrigin: "100px 22px",
                  opacity: bloomed ? 1 : 0,
                  transition: reduce
                    ? "none"
                    : `opacity 0.4s ease-out ${delay + 0.4}s`,
                }}
              >
                <ellipse
                  cx="-2"
                  cy="-2"
                  rx="2.5"
                  ry="5"
                  fill={accent}
                  fillOpacity="0.5"
                  transform="rotate(-30)"
                />
                <ellipse
                  cx="2"
                  cy="-2"
                  rx="2.5"
                  ry="5"
                  fill={accent}
                  fillOpacity="0.5"
                  transform="rotate(30)"
                />
              </g>
            )}
          </g>
        );
      })}

      {/* Centre medallion */}
      <g
        transform="translate(100 100)"
        style={{
          opacity: bloomed ? 1 : 0,
          transform: bloomed ? "translate(100px, 100px) scale(1)" : "translate(100px, 100px) scale(0.3)",
          transformOrigin: "100px 100px",
          transition: reduce
            ? "none"
            : "opacity 0.6s ease-out 1.8s, transform 0.8s cubic-bezier(0.34, 1.56, 0.64, 1) 1.7s",
        }}
      >
        <circle r="14" fill={accent} fillOpacity="0.12" stroke={accent} strokeWidth="0.6" />
        <circle r="6" fill={saffron} fillOpacity="0.85" />
        <circle r="2.5" fill={accent} />
        {Array.from({ length: 8 }).map((_, i) => {
          const a = (i * 45 * Math.PI) / 180;
          return (
            <line
              key={i}
              x1={Math.cos(a) * 6}
              y1={Math.sin(a) * 6}
              x2={Math.cos(a) * 14}
              y2={Math.sin(a) * 14}
              stroke={accent}
              strokeWidth="0.45"
            />
          );
        })}
      </g>
    </svg>
  );
}

// ─── Botanical divider — vine + bud row ───────────────────────────────
function VineDivider({ accent, saffron }: { accent: string; saffron: string }) {
  return (
    <div
      aria-hidden
      className="sumalak-vine-divider"
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "20px 24px",
        gap: 20,
        opacity: 0.85,
        overflow: "hidden",
      }}
    >
      <svg width="180" height="20" viewBox="0 0 180 20" style={{ flexShrink: 1, minWidth: 0 }}>
        <path d="M 0 10 Q 30 2 60 10 T 120 10 T 180 10" stroke={accent} strokeWidth="0.7" fill="none" />
        <ellipse cx="30" cy="6" rx="3" ry="5" fill={accent} fillOpacity="0.5" transform="rotate(-30 30 6)" />
        <ellipse cx="90" cy="14" rx="3" ry="5" fill={accent} fillOpacity="0.5" transform="rotate(30 90 14)" />
        <ellipse cx="150" cy="6" rx="3" ry="5" fill={accent} fillOpacity="0.5" transform="rotate(-30 150 6)" />
        <circle cx="60" cy="10" r="2" fill={saffron} />
        <circle cx="120" cy="10" r="2" fill={saffron} />
      </svg>
      <O.Diamond size={10} color={saffron} />
      <svg width="180" height="20" viewBox="0 0 180 20" style={{ flexShrink: 1, minWidth: 0 }}>
        <path d="M 0 10 Q 30 18 60 10 T 120 10 T 180 10" stroke={accent} strokeWidth="0.7" fill="none" />
        <ellipse cx="30" cy="14" rx="3" ry="5" fill={accent} fillOpacity="0.5" transform="rotate(30 30 14)" />
        <ellipse cx="90" cy="6" rx="3" ry="5" fill={accent} fillOpacity="0.5" transform="rotate(-30 90 6)" />
        <ellipse cx="150" cy="14" rx="3" ry="5" fill={accent} fillOpacity="0.5" transform="rotate(30 150 14)" />
        <circle cx="60" cy="10" r="2" fill={saffron} />
        <circle cx="120" cy="10" r="2" fill={saffron} />
      </svg>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// VARIANT
// ═══════════════════════════════════════════════════════════════════════
export function BotanicalVariant({ t, isDark, ctx }: VariantProps) {
  const tt = useT();
  const reduce = usePrefersReducedMotion();
  const { play } = useSound();

  const photos = t.photos || [];
  // Saffron secondary — fixed across the variant regardless of accent.
  const saffron = "#D4A04B";

  // Fade-in on first paint for hero copy.
  const [heroIn, setHeroIn] = useState(reduce);
  useEffect(() => {
    if (reduce) {
      setHeroIn(true);
      return;
    }
    const id = window.requestAnimationFrame(() => setHeroIn(true));
    return () => window.cancelAnimationFrame(id);
  }, [reduce]);

  // Sound: chime on RSVP yes, tap on RSVP no — wraps the setter.
  const baseSetRsvp = ctx.rsvp.setRsvp;
  const setRsvpWithSound = useCallback(
    (v: string | null) => {
      if (v === "yes") play("chime");
      else if (v === "no") play("tap");
      baseSetRsvp(v);
    },
    [baseSetRsvp, play],
  );

  // Mobile soundbudget: no per-section swooshes, no CTA taps. The chime on
  // RSVP yes survives because that's the "submit" moment.
  const isMobile = useCallback(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return false;
    return window.matchMedia("(max-width: 768px)").matches;
  }, []);

  const onCtaTap = useCallback(() => {
    if (isMobile()) return;
    play("tap");
  }, [play, isMobile]);

  // Swoosh on first hero reveal — scoped to one play.
  const swooshFiredRef = useRef(false);
  const heroSwoosh = useCallback(() => {
    if (swooshFiredRef.current) return;
    swooshFiredRef.current = true;
    if (isMobile()) return;
    play("swoosh");
  }, [play, isMobile]);

  // Fire swoosh when the hero finishes initial fade-in.
  useEffect(() => {
    if (!heroIn) return;
    const t0 = window.setTimeout(() => heroSwoosh(), reduce ? 0 : 600);
    return () => window.clearTimeout(t0);
  }, [heroIn, heroSwoosh, reduce]);

  return (
    <div className="sumalak-variant">
      {/* ─── HERO ────────────────────────────────────────────────────── */}
      <section
        className="sumalak-hero"
        style={{
          minHeight: "100vh",
          position: "relative",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          textAlign: "center",
          padding: "60px 24px",
        }}
      >
        {/* Full-bleed photo with emerald multiply wash */}
        <div
          aria-hidden
          style={{
            position: "absolute",
            inset: 0,
            zIndex: 0,
          }}
        >
          <GuestPhoto
            src={photos[0]}
            style={{ position: "absolute", inset: 0, width: "100%", height: "100%" }}
            fallbackBg={t.bg}
            fallbackTone={t.accent}
          />
          {/* Cream wash to keep type readable */}
          <div
            style={{
              position: "absolute",
              inset: 0,
              background: `linear-gradient(180deg, ${t.bg}f0 0%, ${t.bg}b8 35%, ${t.bg}d8 70%, ${t.bg}f4 100%)`,
            }}
          />
          {/* Emerald multiply tint */}
          <div
            style={{
              position: "absolute",
              inset: 0,
              background: t.accent,
              opacity: 0.16,
              mixBlendMode: "multiply",
            }}
          />
        </div>

        {/* Floral SVG corners — top-left + bottom-right (and TR/BL) */}
        <div
          className="sumalak-corner sumalak-corner-tl"
          style={{ position: "absolute", top: 0, left: 0, color: t.accent, zIndex: 1, opacity: 0.85 }}
          aria-hidden
        >
          <FloralCorner accent={t.accent} saffron={saffron} />
        </div>
        <div
          className="sumalak-corner sumalak-corner-br"
          style={{ position: "absolute", bottom: 0, right: 0, color: t.accent, zIndex: 1, opacity: 0.85 }}
          aria-hidden
        >
          <FloralCorner accent={t.accent} saffron={saffron} flipX flipY />
        </div>
        <div
          className="sumalak-corner sumalak-corner-tr"
          style={{ position: "absolute", top: 0, right: 0, color: t.accent, zIndex: 1, opacity: 0.55 }}
          aria-hidden
        >
          <FloralCorner accent={t.accent} saffron={saffron} flipX size={170} />
        </div>
        <div
          className="sumalak-corner sumalak-corner-bl"
          style={{ position: "absolute", bottom: 0, left: 0, color: t.accent, zIndex: 1, opacity: 0.55 }}
          aria-hidden
        >
          <FloralCorner accent={t.accent} saffron={saffron} flipY size={170} />
        </div>

        {/* Hero content */}
        <div
          className="sumalak-hero-inner"
          style={{
            position: "relative",
            zIndex: 2,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            maxWidth: 720,
            width: "100%",
          }}
        >
          {/* Eyebrow */}
          <div
            style={{
              fontFamily: "var(--font-ui)",
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: "0.5em",
              color: t.accent,
              marginBottom: 28,
              opacity: heroIn ? 1 : 0,
              transition: reduce ? "none" : "opacity 0.8s ease-out 0.2s",
            }}
          >
            NAVRUZ · 21 MARCH 2026
          </div>

          {/* Signature: animated floral wreath above names */}
          <div
            style={{
              color: t.accent,
              marginBottom: 24,
              opacity: heroIn ? 1 : 0,
              transition: reduce ? "none" : "opacity 0.8s ease-out 0.4s",
            }}
          >
            <FloralWreath accent={t.accent} saffron={saffron} reduce={reduce} size={200} />
          </div>

          {/* Names — Cinzel roman caps with letterspacing */}
          <h1
            className="sumalak-couple-names"
            style={{
              fontFamily: t.font,
              fontSize: "clamp(40px, 7vw, 84px)",
              fontWeight: 500,
              letterSpacing: "0.08em",
              lineHeight: 1.05,
              color: t.ink,
              margin: 0,
              textTransform: "uppercase",
              opacity: heroIn ? 1 : 0,
              transform: heroIn ? "translateY(0)" : "translateY(16px)",
              transition: reduce
                ? "none"
                : "opacity 1.2s ease-out 0.7s, transform 1.2s ease-out 0.7s",
            }}
          >
            {t.names.split("&")[0]?.trim()}
            <span
              className="sumalak-amp"
              style={{
                color: saffron,
                fontStyle: "italic",
                margin: "0 .35em",
                fontWeight: 400,
                textTransform: "none",
                letterSpacing: "0",
              }}
            >
              &
            </span>
            {t.names.split("&")[1]?.trim()}
          </h1>

          {/* Saffron flourish under names */}
          <svg
            width="200"
            height="22"
            viewBox="0 0 200 22"
            aria-hidden
            style={{
              marginTop: 24,
              opacity: heroIn ? 1 : 0,
              transition: reduce ? "none" : "opacity 0.8s ease-out 1.0s",
            }}
          >
            <path d="M 4 11 Q 50 4 100 11 T 196 11" stroke={saffron} strokeWidth="1.2" fill="none" />
            <circle cx="100" cy="11" r="3" fill={saffron} />
            <circle cx="40" cy="11" r="1.6" fill={t.accent} />
            <circle cx="160" cy="11" r="1.6" fill={t.accent} />
            <ellipse cx="70" cy="11" rx="2" ry="3.5" fill={t.accent} fillOpacity="0.5" transform="rotate(-25 70 11)" />
            <ellipse cx="130" cy="11" rx="2" ry="3.5" fill={t.accent} fillOpacity="0.5" transform="rotate(25 130 11)" />
          </svg>

          {/* Date + venue */}
          <div
            style={{
              marginTop: 28,
              fontFamily: t.font,
              fontSize: "clamp(16px, 2vw, 22px)",
              letterSpacing: "0.18em",
              color: t.accent,
              textTransform: "uppercase",
              opacity: heroIn ? 1 : 0,
              transition: reduce ? "none" : "opacity 0.8s ease-out 1.2s",
            }}
          >
            {t.date} · {t.venue}
          </div>

          <div
            style={{
              marginTop: 20,
              fontFamily: "var(--font-body)",
              fontStyle: "italic",
              fontSize: 15,
              color: `${t.ink}aa`,
              opacity: heroIn ? 1 : 0,
              transition: reduce ? "none" : "opacity 0.8s ease-out 1.4s",
            }}
          >
            {tt("variant.w06.hero.tagline")}
          </div>
        </div>
      </section>

      <VineDivider accent={t.accent} saffron={saffron} />

      {/* ─── COUNTDOWN ─────────────────────────────────────────────── */}
      <Reveal reduce={reduce}>
        <CountdownSection t={t} {...ctx.countdown} isDark={isDark} />
      </Reveal>

      <VineDivider accent={t.accent} saffron={saffron} />

      {/* ─── STORY ─────────────────────────────────────────────────── */}
      <Reveal reduce={reduce}>
        <StorySection
          t={t}
          eyebrow={tt("variant.w06.story.eyebrow")}
          title={tt("variant.w06.story.title")}
          body={tt("variant.w06.story.body")}
        />
      </Reveal>

      <VineDivider accent={t.accent} saffron={saffron} />

      {/* ─── NAVRUZ OSH (Sumalak) — unique cultural callout ─────────── */}
      <Reveal reduce={reduce}>
        <section
          className="sumalak-navruz"
          style={{
            padding: "120px 32px",
            background: `${t.accent}10`,
            position: "relative",
            textAlign: "center",
            overflow: "hidden",
          }}
        >
          {/* Botanical accents — corner sprigs */}
          <div
            aria-hidden
            style={{
              position: "absolute",
              top: 18,
              left: 18,
              color: t.accent,
              opacity: 0.5,
            }}
          >
            <FloralCorner accent={t.accent} saffron={saffron} size={120} />
          </div>
          <div
            aria-hidden
            style={{
              position: "absolute",
              bottom: 18,
              right: 18,
              color: t.accent,
              opacity: 0.5,
            }}
          >
            <FloralCorner accent={t.accent} saffron={saffron} flipX flipY size={120} />
          </div>

          <div style={{ position: "relative", zIndex: 1, maxWidth: 780, margin: "0 auto" }}>
            <div
              style={{
                fontFamily: "var(--font-ui)",
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: "0.5em",
                color: t.accent,
                marginBottom: 16,
              }}
            >
              {tt("variant.w06.navruz.eyebrow")}
            </div>
            <h2
              style={{
                fontFamily: t.font,
                fontSize: "clamp(36px, 5vw, 56px)",
                fontWeight: 500,
                letterSpacing: "0.06em",
                color: t.ink,
                marginBottom: 20,
                textTransform: "uppercase",
              }}
            >
              {tt("variant.w06.navruz.title")}
            </h2>

            {/* Sumalak pot illustration */}
            <div
              aria-hidden
              style={{
                display: "flex",
                justifyContent: "center",
                margin: "8px 0 28px",
              }}
            >
              <svg width="140" height="90" viewBox="0 0 140 90">
                {/* Cauldron */}
                <ellipse cx="70" cy="78" rx="44" ry="6" fill={t.ink} fillOpacity="0.18" />
                <path
                  d="M 28 50 Q 28 80 70 80 Q 112 80 112 50 L 108 36 L 32 36 Z"
                  fill={t.accent}
                  fillOpacity="0.18"
                  stroke={t.accent}
                  strokeWidth="0.9"
                />
                <ellipse cx="70" cy="36" rx="40" ry="6" fill={saffron} fillOpacity="0.55" stroke={t.accent} strokeWidth="0.7" />
                {/* Steam tendrils */}
                <path d="M 50 32 Q 48 22 54 18 Q 50 10 56 4" stroke={t.accent} strokeWidth="0.7" fill="none" opacity="0.6" />
                <path d="M 70 30 Q 72 18 66 12 Q 72 6 68 0" stroke={t.accent} strokeWidth="0.7" fill="none" opacity="0.6" />
                <path d="M 90 32 Q 88 22 94 18 Q 88 10 94 2" stroke={t.accent} strokeWidth="0.7" fill="none" opacity="0.6" />
                {/* Wheat sprouts on either side */}
                <g stroke={t.accent} strokeWidth="0.6" fill="none">
                  <line x1="14" y1="78" x2="14" y2="58" />
                  <ellipse cx="14" cy="56" rx="1.4" ry="3" fill={saffron} fillOpacity="0.7" stroke="none" />
                  <line x1="20" y1="78" x2="20" y2="62" />
                  <ellipse cx="20" cy="60" rx="1.4" ry="3" fill={saffron} fillOpacity="0.7" stroke="none" />
                  <line x1="120" y1="78" x2="120" y2="58" />
                  <ellipse cx="120" cy="56" rx="1.4" ry="3" fill={saffron} fillOpacity="0.7" stroke="none" />
                  <line x1="126" y1="78" x2="126" y2="62" />
                  <ellipse cx="126" cy="60" rx="1.4" ry="3" fill={saffron} fillOpacity="0.7" stroke="none" />
                </g>
              </svg>
            </div>

            <p
              style={{
                fontFamily: "var(--font-body)",
                fontSize: 18,
                lineHeight: 1.75,
                fontStyle: "italic",
                color: `${t.ink}d8`,
                marginBottom: 24,
              }}
            >
              {tt("variant.w06.navruz.body")}
            </p>
            <p
              style={{
                fontFamily: t.font,
                fontStyle: "italic",
                fontSize: 22,
                letterSpacing: "0.04em",
                color: t.accent,
              }}
            >
              {tt("variant.w06.navruz.proverb")}
            </p>

            {/* Trio of botanical badges */}
            <div
              className="sumalak-navruz-badges"
              style={{
                display: "flex",
                justifyContent: "center",
                gap: 32,
                marginTop: 40,
                flexWrap: "wrap",
              }}
            >
              {[
                { label: tt("variant.w06.navruz.badge_0_label"), sub: tt("variant.w06.navruz.badge_0_sub") },
                { label: tt("variant.w06.navruz.badge_1_label"), sub: tt("variant.w06.navruz.badge_1_sub") },
                { label: tt("variant.w06.navruz.badge_2_label"), sub: tt("variant.w06.navruz.badge_2_sub") },
              ].map((b) => (
                <div
                  key={b.label}
                  style={{
                    minWidth: 140,
                    padding: "16px 14px",
                    background: t.bg,
                    border: `1px solid ${t.accent}66`,
                    textAlign: "center",
                  }}
                >
                  <div
                    style={{
                      fontFamily: t.font,
                      fontSize: 17,
                      letterSpacing: "0.06em",
                      color: t.ink,
                      textTransform: "uppercase",
                    }}
                  >
                    {b.label}
                  </div>
                  <div
                    style={{
                      fontFamily: "var(--font-body)",
                      fontStyle: "italic",
                      fontSize: 13,
                      color: saffron,
                      marginTop: 4,
                    }}
                  >
                    {b.sub}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      </Reveal>

      <VineDivider accent={t.accent} saffron={saffron} />

      {/* ─── GALLERY ───────────────────────────────────────────────── */}
      <Reveal reduce={reduce}>
        <GallerySection t={t} />
      </Reveal>

      <VineDivider accent={t.accent} saffron={saffron} />

      {/* ─── SCHEDULE ──────────────────────────────────────────────── */}
      <Reveal reduce={reduce}>
        <div
          className="sumalak-schedule-wrap"
          onClick={(e) => {
            const target = e.target as HTMLElement | null;
            if (target && target.closest("button")) onCtaTap();
          }}
        >
          <ScheduleSection
            t={t}
            items={[
              ["10:00", tt("variant.w06.schedule.0.event")],
              ["12:00", tt("variant.w06.schedule.1.event")],
              ["13:30", tt("variant.w06.schedule.2.event")],
              ["16:00", tt("variant.w06.schedule.3.event")],
              ["19:00", tt("variant.w06.schedule.4.event")],
              ["22:00", tt("variant.w06.schedule.5.event")],
            ]}
          />
        </div>
      </Reveal>

      <VineDivider accent={t.accent} saffron={saffron} />

      {/* ─── MAP ───────────────────────────────────────────────────── */}
      <Reveal reduce={reduce}>
        <div
          className="sumalak-map-wrap"
          onClick={(e) => {
            const target = e.target as HTMLElement | null;
            if (target && (target.closest("a") || target.closest("button"))) onCtaTap();
          }}
        >
          <MapSection
            t={t}
            isDark={isDark}
            addressLine={tt("variant.w06.map.address")}
          />
        </div>
      </Reveal>

      <VineDivider accent={t.accent} saffron={saffron} />

      {/* ─── RSVP ──────────────────────────────────────────────────── */}
      <Reveal reduce={reduce}>
        <RsvpSection
          t={t}
          isDark={isDark}
          rsvp={ctx.rsvp.rsvp}
          setRsvp={setRsvpWithSound}
          guests={ctx.rsvp.guests}
          setGuests={ctx.rsvp.setGuests}
          guestName={ctx.rsvp.guestName}
          setGuestName={ctx.rsvp.setGuestName}
          slug={ctx.slug}
          label={tt("variant.w06.rsvp.label")}
        />
      </Reveal>

      <VineDivider accent={t.accent} saffron={saffron} />

      {/* ─── GUESTBOOK ─────────────────────────────────────────────── */}
      <Reveal reduce={reduce}>
        <GuestbookSection
          t={t}
          isDark={isDark}
          wishes={ctx.guestbook.wishes}
          setWishes={ctx.guestbook.setWishes}
          guestName={ctx.guestbook.guestName}
          setGuestName={ctx.guestbook.setGuestName}
          wish={ctx.guestbook.wish}
          setWish={ctx.guestbook.setWish}
          slug={ctx.slug}
        />
      </Reveal>

      <VineDivider accent={t.accent} saffron={saffron} />

      {/* ─── SHARE ─────────────────────────────────────────────────── */}
      <Reveal reduce={reduce}>
        <div
          className="sumalak-share-wrap"
          onClick={(e) => {
            const target = e.target as HTMLElement | null;
            if (target && target.closest("button")) onCtaTap();
          }}
        >
          <ShareSection t={t} />
        </div>
      </Reveal>

      <GuestFooter t={t} />

      {/* ──────────────────────────────────────────────────────────────
          Mobile rules — additive only. Scoped to .sumalak-variant.
          ────────────────────────────────────────────────────────────── */}
      <style>{`
        @media (max-width: 768px) {
          .sumalak-variant .sumalak-hero {
            padding: 64px 18px 48px !important;
            min-height: auto !important;
          }
          .sumalak-variant .sumalak-navruz-grid {
            grid-template-columns: 1fr !important;
            gap: 32px !important;
          }
          .sumalak-variant .sumalak-schedule-wrap,
          .sumalak-variant .sumalak-map-wrap,
          .sumalak-variant .sumalak-share-wrap {
            padding-left: 18px !important;
            padding-right: 18px !important;
          }
          /* Smaller corners on mobile so the photo + names breathe. */
          .sumalak-variant .sumalak-corner-tl svg,
          .sumalak-variant .sumalak-corner-br svg { width: 140px !important; height: 140px !important; }
          .sumalak-variant .sumalak-corner-tr,
          .sumalak-variant .sumalak-corner-bl { display: none !important; }

          .sumalak-variant .sumalak-couple-names {
            font-size: clamp(36px, 12vw, 64px) !important;
            letter-spacing: 0.06em !important;
            line-height: 1.08 !important;
          }
          .sumalak-variant .sumalak-amp {
            display: inline-block;
            margin: 0 .25em !important;
          }
          .sumalak-variant .sumalak-navruz {
            padding: 80px 22px !important;
          }
          .sumalak-variant .sumalak-navruz h2 {
            font-size: 28px !important;
          }
          /* Navruz badge trio: shrink min-width so all three fit on one row */
          .sumalak-variant .sumalak-navruz-badges > div {
            min-width: 0 !important;
            flex: 1 1 80px !important;
          }
        }
        @media (prefers-reduced-motion: reduce) {
          .sumalak-variant *,
          .sumalak-variant *::before,
          .sumalak-variant *::after {
            animation: none !important;
            transition: none !important;
          }
        }
      `}</style>
    </div>
  );
}
